
import { Component, Vue } from "vue-property-decorator";

interface MenuSection {
  label: string;
  target: string;
}

interface InertElementState {
  element: HTMLElement;
  hadInert: boolean;
}

@Component
export default class CatalystMenu extends Vue {
  isOpen = false;
  activeSection = "slide-about-us";
  hoveredSection: string | null = null;
  focusedSection: string | null = null;
  private inertElements: InertElementState[] = [];

  readonly sections: MenuSection[] = [
    { label: "About us", target: "slide-about-us" },
    { label: "Our work", target: "slide-portfolio" },
    { label: "Our partners", target: "slide-partners" },
    { label: "Our people", target: "slide-team" },
    { label: "Contact us", target: "slide-contact" },
  ];

  get previewSection() {
    return this.hoveredSection || this.focusedSection;
  }

  get highlightedSection() {
    return this.previewSection || this.activeSection;
  }

  mounted() {
    document.body.appendChild(this.$el);
    this.syncContentInteractivity();
    this.updateActiveSection();
    window.addEventListener("scroll", this.updateActiveSection, {
      passive: true,
    });
    window.addEventListener("resize", this.updateActiveSection, {
      passive: true,
    });
    window.addEventListener("keydown", this.onKeydown);
  }

  beforeDestroy() {
    if (this.isOpen) this.restoreBackgroundInteractivity();
    window.removeEventListener("scroll", this.updateActiveSection);
    window.removeEventListener("resize", this.updateActiveSection);
    window.removeEventListener("keydown", this.onKeydown);
    this.$el.parentNode?.removeChild(this.$el);
  }

  preview(target: string, event: PointerEvent) {
    if (event.pointerType !== "touch") this.hoveredSection = target;
  }

  clearPreview(target: string) {
    if (this.hoveredSection === target) this.hoveredSection = null;
  }

  setOpen(value: boolean, restoreFocus = false, triggerEvent?: MouseEvent) {
    if (this.isOpen === value) return;
    this.isOpen = value;
    this.hoveredSection = null;
    this.focusedSection = null;

    if (value) {
      this.updateActiveSection();
      this.disableBackgroundInteractivity();
    } else {
      this.restoreBackgroundInteractivity();
    }

    this.$nextTick(() => {
      this.syncContentInteractivity();
      // Pointer-triggered focus is rendered as a persistent focus-visible ring
      // by iOS Safari. Leave focus on the toggle for taps/clicks, while still
      // moving keyboard users to the current section.
      if (value && (!triggerEvent || triggerEvent.detail === 0)) {
        const activeIndex = this.sections.findIndex(
          (section) => section.target === this.activeSection
        );
        this.navigationLinks[Math.max(activeIndex, 0)]?.focus({
          preventScroll: true,
        });
      } else if (restoreFocus)
        (this.$refs.toggle as HTMLButtonElement | undefined)?.focus({
          preventScroll: true,
        });
    });
  }

  navigate(target: string) {
    this.activeSection = target;
    this.setOpen(false);

    const caseStudyBack = document.querySelector<HTMLButtonElement>(
      ".case-study-overlay .case-study-back"
    );
    caseStudyBack?.click();

    window.setTimeout(
      () => {
        const destination = document.getElementById(target);
        if (!destination) return;

        const behavior = window.matchMedia("(prefers-reduced-motion: reduce)")
          .matches
          ? "auto"
          : "smooth";

        if (target === "slide-partners") {
          const partnersRunway = destination.closest(
            ".slide-Partners"
          ) as HTMLElement | null;
          if (partnersRunway) {
            const runwayRect = partnersRunway.getBoundingClientRect();
            window.scrollTo({
              top: Math.max(
                0,
                window.scrollY +
                  runwayRect.top +
                  runwayRect.height -
                  window.innerHeight
              ),
              behavior,
            });
            return;
          }
        }

        destination.scrollIntoView({
          behavior,
          block: "start",
        });
      },
      caseStudyBack ? 180 : 0
    );
  }

  private get navigationLinks() {
    const refs = this.$refs.navLinks as HTMLElement[] | HTMLElement | undefined;
    if (!refs) return [];
    return Array.isArray(refs) ? refs : [refs];
  }

  private get focusableElements() {
    return [
      this.$refs.toggle as HTMLElement | undefined,
      ...this.navigationLinks,
      this.$refs.contact as HTMLElement | undefined,
    ].filter((element): element is HTMLElement => Boolean(element));
  }

  private syncContentInteractivity() {
    const content = this.$refs.content as
      | (HTMLElement & { inert: boolean })
      | undefined;
    if (content) content.inert = !this.isOpen;
  }

  private disableBackgroundInteractivity() {
    this.inertElements = Array.from(document.body.children)
      .filter(
        (element) => element !== this.$el && element instanceof HTMLElement
      )
      .map((element) => ({
        element: element as HTMLElement,
        hadInert: element.hasAttribute("inert"),
      }));
    this.inertElements.forEach(({ element }) =>
      element.setAttribute("inert", "")
    );
  }

  private restoreBackgroundInteractivity() {
    this.inertElements.forEach(({ element, hadInert }) => {
      if (!hadInert) element.removeAttribute("inert");
    });
    this.inertElements = [];
  }

  private updateActiveSection() {
    this.hoveredSection = null;
    this.focusedSection = null;

    const viewportMarker = window.innerHeight * 0.42;
    let closest = this.sections[0];
    let closestDistance = Number.POSITIVE_INFINITY;

    this.sections.forEach((section) => {
      const element = document.getElementById(section.target);
      if (!element) return;
      const rect = element.getBoundingClientRect();
      const distance =
        viewportMarker < rect.top
          ? rect.top - viewportMarker
          : viewportMarker > rect.bottom
          ? viewportMarker - rect.bottom
          : 0;
      if (distance < closestDistance) {
        closest = section;
        closestDistance = distance;
      }
    });

    this.activeSection = closest.target;
  }

  private onKeydown(event: KeyboardEvent) {
    if (!this.isOpen) return;
    if (event.key === "Escape") {
      event.preventDefault();
      this.setOpen(false, true);
      return;
    }

    const controls = this.focusableElements;
    const currentIndex = controls.indexOf(
      document.activeElement as HTMLElement
    );
    if (event.key === "Tab") {
      if (event.shiftKey && currentIndex <= 0) {
        event.preventDefault();
        controls[controls.length - 1]?.focus();
      } else if (!event.shiftKey && currentIndex === controls.length - 1) {
        event.preventDefault();
        controls[0]?.focus();
      }
      return;
    }

    if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) return;
    event.preventDefault();
    const links = this.navigationLinks;
    const linkIndex = links.indexOf(document.activeElement as HTMLElement);
    let nextIndex = 0;
    if (event.key === "End") nextIndex = links.length - 1;
    else if (event.key === "ArrowUp")
      nextIndex =
        linkIndex < 0
          ? links.length - 1
          : (linkIndex - 1 + links.length) % links.length;
    else if (event.key === "ArrowDown")
      nextIndex = linkIndex < 0 ? 0 : (linkIndex + 1) % links.length;
    links[nextIndex]?.focus({ preventScroll: true });
  }
}
